import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { CheckCircle2, Upload, ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';
import { onboardingSteps } from '../mock';

const OnboardingPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1 - Personal
    firstName: '',
    lastName: '',
    cedula: '',
    phone: '',
    email: '',
    // Step 2 - Finca
    farmName: '',
    farmLocation: '',
    farmSize: '',
    activityType: '',
    // Step 3 - Documents
    cedulaFile: null,
    rifFile: null,
    propertyFile: null
  });
  const navigate = useNavigate();

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileChange = (field, file) => {
    setFormData({ ...formData, [field]: file });
  };

  const validateStep = () => {
    if (currentStep === 1) {
      if (!formData.firstName || !formData.lastName || !formData.cedula || !formData.phone || !formData.email) {
        toast({
          title: 'Campos incompletos',
          description: 'Por favor completa todos los campos del paso 1',
          variant: 'destructive'
        });
        return false;
      }
    } else if (currentStep === 2) {
      if (!formData.farmName || !formData.farmLocation || !formData.farmSize || !formData.activityType) {
        toast({
          title: 'Campos incompletos',
          description: 'Por favor completa todos los campos del paso 2',
          variant: 'destructive'
        });
        return false;
      }
    } else if (currentStep === 3) {
      if (!formData.cedulaFile || !formData.rifFile || !formData.propertyFile) {
        toast({
          title: 'Documentos incompletos',
          description: 'Por favor sube todos los documentos requeridos',
          variant: 'destructive'
        });
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    toast({
      title: '¡Solicitud enviada exitosamente!',
      description: 'Te contactaremos en las próximas 24-48 horas para continuar el proceso.',
    });
    setTimeout(() => {
      navigate('/');
    }, 2000);
  };

  const progress = (currentStep / 4) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100/30">
      <Toaster />
      
      {/* Header */}
      <header className="bg-white border-b border-green-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_5732c38f-46bd-4a56-9931-c3cf86a70f4f/artifacts/nfkada7y_logo.jpg" alt="Banco Agrícola" className="h-10 w-auto" />
            </Link>
            <Link to="/">
              <Button variant="ghost">Cancelar</Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Progress Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Apertura de Cuenta Agrícola</h1>
            <p className="text-gray-600 mb-6">Completa los siguientes pasos para abrir tu cuenta</p>
            <Progress value={progress} className="h-2" />
            <div className="flex justify-between mt-4">
              {onboardingSteps.map((step) => (
                <div key={step.step} className={`flex items-center gap-2 ${
                  currentStep >= step.step ? 'text-green-600' : 'text-gray-400'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                    currentStep > step.step ? 'bg-green-600 border-green-600 text-white' : 
                    currentStep === step.step ? 'border-green-600 text-green-600' : 
                    'border-gray-300 text-gray-400'
                  }`}>
                    {currentStep > step.step ? <CheckCircle2 className="w-5 h-5" /> : step.step}
                  </div>
                  <span className="text-sm font-medium hidden md:inline">{step.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <Card>
            <CardHeader>
              <CardTitle>{onboardingSteps[currentStep - 1].title}</CardTitle>
              <CardDescription>{onboardingSteps[currentStep - 1].description}</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">Nombre *</Label>
                      <Input 
                        id="firstName" 
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        placeholder="Carlos"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Apellido *</Label>
                      <Input 
                        id="lastName" 
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        placeholder="Rodríguez"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="cedula">Cédula de Identidad *</Label>
                    <Input 
                      id="cedula" 
                      value={formData.cedula}
                      onChange={(e) => handleInputChange('cedula', e.target.value)}
                      placeholder="V-12345678"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Teléfono *</Label>
                    <Input 
                      id="phone" 
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+58 412-555-0100"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Correo Electrónico *</Label>
                    <Input 
                      id="email" 
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="carlos@email.com"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Farm Information */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="farmName">Nombre de la Finca/Empresa *</Label>
                    <Input 
                      id="farmName" 
                      value={formData.farmName}
                      onChange={(e) => handleInputChange('farmName', e.target.value)}
                      placeholder="Finca El Progreso"
                    />
                  </div>
                  <div>
                    <Label htmlFor="farmLocation">Ubicación (Estado/Municipio) *</Label>
                    <Input 
                      id="farmLocation" 
                      value={formData.farmLocation}
                      onChange={(e) => handleInputChange('farmLocation', e.target.value)}
                      placeholder="Yaracuy, Municipio Independencia"
                    />
                  </div>
                  <div>
                    <Label htmlFor="farmSize">Tamaño de la Finca (Hectáreas) *</Label>
                    <Input 
                      id="farmSize" 
                      type="number"
                      value={formData.farmSize}
                      onChange={(e) => handleInputChange('farmSize', e.target.value)}
                      placeholder="50"
                    />
                  </div>
                  <div>
                    <Label htmlFor="activityType">Tipo de Actividad *</Label>
                    <Select value={formData.activityType} onValueChange={(value) => handleInputChange('activityType', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona una opción" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agricultura">Agricultura</SelectItem>
                        <SelectItem value="ganaderia">Ganadería</SelectItem>
                        <SelectItem value="avicultura">Avicultura</SelectItem>
                        <SelectItem value="piscicultura">Piscicultura</SelectItem>
                        <SelectItem value="mixto">Mixto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Step 3: Documents */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="cedulaFile">Cédula de Identidad (PDF/JPG) *</Label>
                    <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 transition-colors cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <Input 
                        id="cedulaFile" 
                        type="file"
                        onChange={(e) => handleFileChange('cedulaFile', e.target.files[0])}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                      <label htmlFor="cedulaFile" className="cursor-pointer">
                        <p className="text-sm text-gray-600">
                          {formData.cedulaFile ? formData.cedulaFile.name : 'Haz clic para subir o arrastra el archivo'}
                        </p>
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="rifFile">RIF Agrícola (PDF/JPG) *</Label>
                    <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 transition-colors cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <Input 
                        id="rifFile" 
                        type="file"
                        onChange={(e) => handleFileChange('rifFile', e.target.files[0])}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                      <label htmlFor="rifFile" className="cursor-pointer">
                        <p className="text-sm text-gray-600">
                          {formData.rifFile ? formData.rifFile.name : 'Haz clic para subir o arrastra el archivo'}
                        </p>
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="propertyFile">Documento de Propiedad/Arrendamiento (PDF/JPG) *</Label>
                    <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-500 transition-colors cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <Input 
                        id="propertyFile" 
                        type="file"
                        onChange={(e) => handleFileChange('propertyFile', e.target.files[0])}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                      <label htmlFor="propertyFile" className="cursor-pointer">
                        <p className="text-sm text-gray-600">
                          {formData.propertyFile ? formData.propertyFile.name : 'Haz clic para subir o arrastra el archivo'}
                        </p>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Confirmation */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h3 className="font-semibold text-green-900 mb-4 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      Resumen de tu Solicitud
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div>
                        <p className="text-gray-600">Nombre Completo:</p>
                        <p className="font-medium">{formData.firstName} {formData.lastName}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Cédula:</p>
                        <p className="font-medium">{formData.cedula}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Finca:</p>
                        <p className="font-medium">{formData.farmName} - {formData.farmLocation}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Actividad:</p>
                        <p className="font-medium capitalize">{formData.activityType}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Documentos:</p>
                        <p className="font-medium">✓ Cédula • ✓ RIF • ✓ Propiedad</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      <strong>Próximos pasos:</strong> Una vez enviada tu solicitud, nuestro equipo la revisará y te contactaremos en las próximas 24-48 horas para completar el proceso de apertura de cuenta.
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-6 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Anterior
                </Button>
                {currentStep < 4 ? (
                  <Button
                    onClick={handleNext}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Siguiente
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Enviar Solicitud
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;